import axios from 'axios';

// const API_URL = 'http://localhost:5000/api'; // Replace with your backend URL
// export const imageUrl = 'http://localhost:5000/'; // image url

const API_URL = process.env.REACT_APP_API_URL;
export const imageUrl = process.env.REACT_APP_IMAGE_URL;

const apiClient = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Add a request interceptor to set the token dynamically before each request
apiClient.interceptors.request.use((config) => {
  const token = JSON.parse(localStorage.getItem('user'));
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

apiClient.interceptors.response.use(
  (response) => {
    // ✅ REMOVE maintenance flag when backend is back
    if (localStorage.getItem("maintenance") === "true") {
      console.log("✅ Maintenance OFF detected");
      localStorage.removeItem("maintenance");
      // Notify App.js in the SAME tab — no hard reload needed
      window.dispatchEvent(new Event("maintenanceChange"));
    }
    return response;
  },
  (error) => {
    if (error.response && error.response.status === 503) {
      localStorage.setItem("maintenance", "true");
      // ✅ Notify App.js in the SAME tab (native 'storage' event only fires in other tabs)
      window.dispatchEvent(new Event("maintenanceChange"));
      return new Promise(() => {});
    }

    return Promise.reject(error);
  }
);

// GET request
export const getData = async (endpoint) => {
  try {
    const response = await apiClient.get(endpoint);
    return response.data;
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
};

// Download Csv 
export const downloadCSV = async (endpoint) => {
  try {
    const response = await apiClient.get(endpoint, {
      responseType: "blob",
    });
    return response;
  } catch (error) {
    console.error("Error downloading CSV:", error);
    throw error;
  }
};

// POST request
export const postData = async (endpoint, data) => {
  try {
    const response = await apiClient.post(endpoint, data);
    return response.data;
  } catch (error) {
    console.error('Error posting data:', error);
    throw error;
  }
};
export const postDataWithFile = async (endpoint, data) => {
  try {
    const response = await apiClient.post(endpoint, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error posting data:', error);
    throw error;
  }
};
// file uplode
export const postFileData = async (endpoint, data) => {
  try {
    const response = await apiClient.post(endpoint, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error posting data:', error);
    throw error;
  }
};

export const uploadToS3 = async (presignedUrl, file, { onProgress } = {}) => {
  try {
    const response = await axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
      onUploadProgress: (evt) => {
        if (onProgress && evt && typeof evt.loaded === 'number' && typeof evt.total === 'number') {
          onProgress({ loaded: evt.loaded, total: evt.total });
        }
      },
    });
    return response;
  } catch (error) {
    console.error('Error uploading to S3:', error);
    throw error;
  }
};

// export const getPresignedUrl = async (fileType) => {
//   try {
//     const response = await apiClient.post('/generate-presigned-url', { fileType });
//     return response.data;
//   } catch (error) {
//     console.error('Error getting presigned URL:', error);
//     throw error;
//   }
// };

export const getPresignedUrl = async (fileType) => {
  try {
    const response = await apiClient.post('/generate-presigned-url', { fileType });
    return response.data;
  } catch (error) {
    console.error('Error getting presigned URL:', error);
    throw error;
  }
};

// Multipart upload helpers
export const initiateMultipartUpload = async (fileType) => {
  try {
    const response = await apiClient.post('/multipart/initiate', { fileType });
    return response.data; // { uploadId, fileKey }
  } catch (error) {
    console.error('Error initiating multipart upload:', error);
    throw error;
  }
};

export const getMultipartPartUrl = async ({ fileKey, uploadId, partNumber }) => {
  try {
    const response = await apiClient.get('/multipart/part-url', {
      params: { fileKey, uploadId, partNumber },
    });
    return response.data; // { uploadUrl }
  } catch (error) {
    console.error('Error getting part upload URL:', error);
    throw error;
  }
};

export const completeMultipartUpload = async ({ fileKey, uploadId, parts }) => {
  try {
    const response = await apiClient.post('/multipart/complete', { fileKey, uploadId, parts });
    return response.data; // { fileKey, publicUrl }
  } catch (error) {
    console.error('Error completing multipart upload:', error);
    throw error;
  }
};

export const abortMultipartUpload = async ({ fileKey, uploadId }) => {
  try {
    const response = await apiClient.post('/multipart/abort', { fileKey, uploadId });
    return response.data;
  } catch (error) {
    console.error('Error aborting multipart upload:', error);
    throw error;
  }
};

export const getMultipartPartUrls = async ({ fileKey, uploadId, partNumbers }) => {
  try {
    const response = await apiClient.post('/multipart/part-urls', { fileKey, uploadId, partNumbers });
    return response.data; // { urls: [{ partNumber, url }] }
  } catch (error) {
    console.error('Error getting batch part URLs:', error);
    throw error;
  }
};

export const uploadPartToS3 = async (uploadUrl, blobPart, { onProgress } = {}) => {
  try {
    const response = await axios.put(uploadUrl, blobPart, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
      onUploadProgress: (evt) => {
        if (onProgress && evt && typeof evt.loaded === 'number' && typeof evt.total === 'number') {
          onProgress({ loaded: evt.loaded, total: evt.total });
        }
      },
    });
    // ETag is usually returned in quotes
    const eTag = response.headers?.etag || response.headers?.ETag;
    return { eTag };
  } catch (error) {
    console.error('Error uploading part to S3:', error);
    throw error;
  }
};


// High-level multipart upload orchestrator
// export const multipartUploadFile = async (file, { partSize = 16 * 1024 * 1024, concurrency = 8, onProgress } = {}) => {
//   const { uploadId, fileKey } = await initiateMultipartUpload(file.type);

//   const totalParts = Math.ceil(file.size / partSize);
//   let nextPartNumber = 1;
//   const uploadedParts = [];
//   let hasError = false;
//   const partLoadedMap = new Map();

//   const reportProgress = () => {
//     if (!onProgress) return;
//     let loaded = 0;
//     partLoadedMap.forEach(v => { loaded += v || 0; });
//     const percent = file.size ? Math.min(100, Math.round((loaded / file.size) * 100)) : 0;
//     onProgress(percent);
//   };

//   // Prefetch a window of part URLs to reduce round-trips
//   const urlCache = new Map();
//   const PREFETCH = Math.max(concurrency * 2, 6);

//   const prefetchUrls = async (startPart) => {
//     const toFetch = [];
//     for (let p = startPart; p < startPart + PREFETCH && p <= totalParts; p++) {
//       if (!urlCache.has(p)) toFetch.push(p);
//     }
//     if (toFetch.length === 0) return;
//     const { urls } = await getMultipartPartUrls({ fileKey, uploadId, partNumbers: toFetch });
//     urls.forEach(({ partNumber, url }) => urlCache.set(partNumber, url));
//   };

//   await prefetchUrls(1);

//   const uploadNext = async () => {
//     if (hasError) return;
//     const partNumber = nextPartNumber++;
//     if (partNumber > totalParts) return;
//     const start = (partNumber - 1) * partSize;
//     const end = Math.min(start + partSize, file.size);
//     const blob = file.slice(start, end);
//     if (!urlCache.has(partNumber)) {
//       await prefetchUrls(partNumber);
//     }
//     const uploadUrl = urlCache.get(partNumber);
//     const { eTag } = await uploadPartToS3(uploadUrl, blob, {
//       onProgress: ({ loaded }) => {
//         partLoadedMap.set(partNumber, loaded);
//         reportProgress();
//       },
//     });
//     uploadedParts.push({ ETag: eTag, PartNumber: partNumber });
//     // prefetch ahead for the next window
//     if (partNumber % PREFETCH === 0) {
//       await prefetchUrls(partNumber + 1);
//     }
//     await uploadNext();
//   };

//   const workers = Array.from({ length: Math.min(concurrency, totalParts) }, () => uploadNext());
//   try {
//     await Promise.all(workers);
//   } catch (error) {
//     hasError = true;
//     try { await abortMultipartUpload({ fileKey, uploadId }); } catch (_) {}
//     throw error;
//   }

//   const completion = await completeMultipartUpload({ fileKey, uploadId, parts: uploadedParts });
//   if (onProgress) onProgress(100);
//   return { fileKey, publicUrl: completion.publicUrl };
// };



export const multipartUploadFile = async (
  file,
  {
    partSize = 16 * 1024 * 1024, // 16 MB
    concurrency = 8,
    maxRetries = 3,
    onProgress,
  } = {}
) => {
  const { uploadId, fileKey } = await initiateMultipartUpload(file.type);

  const totalParts = Math.ceil(file.size / partSize);
  let nextPartNumber = 1;
  const uploadedParts = [];
  let hasError = false;
  const partLoadedMap = new Map();

  const reportProgress = () => {
    if (!onProgress) return;
    let loaded = 0;
    partLoadedMap.forEach((v) => {
      loaded += v || 0;
    });
    const percent = file.size
      ? Math.min(100, Math.round((loaded / file.size) * 100))
      : 0;
    onProgress(percent);
  };

  // Prefetch URLs for a batch of parts to reduce round trips
  const urlCache = new Map();
  const PREFETCH = Math.max(concurrency * 2, 8);

  const prefetchUrls = async (startPart) => {
    const toFetch = [];
    for (let p = startPart; p < startPart + PREFETCH && p <= totalParts; p++) {
      if (!urlCache.has(p)) toFetch.push(p);
    }
    if (toFetch.length === 0) return;
    const { urls } = await getMultipartPartUrls({
      fileKey,
      uploadId,
      partNumbers: toFetch,
    });
    urls.forEach(({ partNumber, url }) => urlCache.set(partNumber, url));
  };

  await prefetchUrls(1);

  const uploadPart = async (partNumber, retries = 0) => {
    const start = (partNumber - 1) * partSize;
    const end = Math.min(start + partSize, file.size);
    const blob = file.slice(start, end);

    if (!urlCache.has(partNumber)) {
      await prefetchUrls(partNumber);
    }
    const uploadUrl = urlCache.get(partNumber);

    try {
      const { eTag } = await uploadPartToS3(uploadUrl, blob, {
        onProgress: ({ loaded }) => {
          partLoadedMap.set(partNumber, loaded);
          reportProgress();
        },
      });

      uploadedParts.push({
        ETag: (eTag || "").replace(/"/g, ""), // cleanup quotes
        PartNumber: partNumber,
      });
    } catch (err) {
      if (retries < maxRetries) {
        console.warn(
          `Retrying part ${partNumber}, attempt ${retries + 1}/${maxRetries}`
        );
        return uploadPart(partNumber, retries + 1);
      }
      throw err;
    }
  };

  const uploadNext = async () => {
    if (hasError) return;
    const partNumber = nextPartNumber++;
    if (partNumber > totalParts) return;

    await uploadPart(partNumber);

    // Prefetch ahead for the next window
    if (partNumber % PREFETCH === 0) {
      await prefetchUrls(partNumber + 1);
    }

    await uploadNext();
  };

  const workers = Array.from(
    { length: Math.min(concurrency, totalParts) },
    () => uploadNext()
  );

  try {
    await Promise.all(workers);
  } catch (error) {
    hasError = true;
    try {
      await abortMultipartUpload({ fileKey, uploadId });
    } catch (_) { }
    throw error;
  }

  const completion = await completeMultipartUpload({
    fileKey,
    uploadId,
    parts: uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber),
  });

  if (onProgress) onProgress(100);
  return { fileKey, publicUrl: completion.publicUrl };
};


// PUT request
export const putData = async (endpoint, data) => {
  try {
    const response = await apiClient.put(endpoint, data);
    return response.data;
  } catch (error) {
    console.error('Error updating data:', error);
    throw error;
  }
};

export const putFileData = async (endpoint, data) => {
  try {
    const response = await apiClient.put(endpoint, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating data:', error);
    throw error;
  }
};

// DELETE request
export const deleteData = async (endpoint) => {
  try {
    const response = await apiClient.delete(endpoint);
    return response.data;
  } catch (error) {
    console.error('Error deleting data:', error);
    throw error;
  }
};
